"use client";

import {
  Drawer,
  Button,
  NumberInput,
  Select,
  Switch,
  Text,
  TextInput,
  ActionIcon,
  Box as MantineBox,
  Text as MantineText,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useUiStore } from "@/stores/uiStore";
import { useAdminItemEditor } from "@/hooks/useAdminItemEditor";
import styles from "./styles.module.scss";

const AdminItemSlideout = () => {
  const isOpen = useUiStore((state) => state.adminItemModalOpen);
  const item = useUiStore((state) => state.slideInItem);
  const { closeAdminItemModal } = useUiStore((state) => state.actions);

  const {
    isAdmin,
    form,
    setForm,
    resetForm,
    save,
    hideItem,
    saving,
    vendorOptions,
    categoryOptions,
  } = useAdminItemEditor(item);

  if (!isAdmin) return null;

  return (
    <Drawer
      opened={isOpen}
      onClose={closeAdminItemModal}
      position="right"
      size="md"
      zIndex={10000}
      withCloseButton={false}
      title={
        <MantineBox className={styles.header}>
          <MantineText span className={styles.title}>
            Admin listing editor
          </MantineText>
          <ActionIcon onClick={closeAdminItemModal} variant="subtle" size={36} radius="sm">
            <IconX size={22} style={{ height: "100%", width: "100%" }} />
          </ActionIcon>
        </MantineBox>
      }
      classNames={{ content: styles.drawerContent, header: styles.drawerHeader }}
    >
      {!item ? (
        <Text size="sm" c="dimmed">
          Select a listing to edit.
        </Text>
      ) : (
        <MantineBox className={styles.panel}>
          <MantineBox className={styles.summary}>
            <Text size="sm" fw={600}>
              {item.title}
            </Text>
            <Text size="xs" c="dimmed">
              Listing ID: {item.id}
            </Text>
          </MantineBox>

          <Switch
            label="Mark as sold"
            checked={form.sold}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                sold: event.currentTarget.checked,
              }))
            }
          />

          <TextInput
            label="Title"
            value={form.title}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                title: event.currentTarget.value,
              }))
            }
          />

          <TextInput
            label="Price (raw)"
            value={form.price}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                price: event.currentTarget.value,
              }))
            }
          />

          <NumberInput
            label="Price USD"
            value={form.priceusd}
            onChange={(value) =>
              setForm((current) => ({
                ...current,
                priceusd: typeof value === "number" ? value : undefined,
              }))
            }
            min={0}
            decimalScale={2}
            hideControls
          />

          <TextInput
            label="Currency"
            value={form.currency}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                currency: event.currentTarget.value,
              }))
            }
          />

          <Select
            label="Category"
            searchable
            data={categoryOptions}
            value={form.itemtype}
            onChange={(value) =>
              setForm((current) => ({
                ...current,
                itemtype: value || "",
              }))
            }
          />

          {vendorOptions.length ? (
            <Select
              label="Vendor"
              searchable
              data={vendorOptions}
              value={form.vendorId}
              onChange={(value) =>
                setForm((current) => ({
                  ...current,
                  vendorId: value || "",
                }))
              }
            />
          ) : (
            <TextInput
              label="Vendor ID"
              value={form.vendorId}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  vendorId: event.currentTarget.value,
                }))
              }
            />
          )}

          <MantineBox className={styles.actions}>
            <Button size="sm" onClick={save} loading={saving} disabled={saving}>
              Save changes
            </Button>
            <Button size="sm" variant="subtle" onClick={resetForm} disabled={saving}>
              Reset
            </Button>
            <Button
              size="sm"
              variant="subtle"
              color="red"
              onClick={async () => {
                const hidden = await hideItem();
                if (hidden) closeAdminItemModal();
              }}
              disabled={saving}
            >
              Hide
            </Button>
          </MantineBox>
        </MantineBox>
      )}
    </Drawer>
  );
};

export default AdminItemSlideout;
